import React from 'react';
import { Box, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import AIOverviewCard from 'src/App/Dashboards/Market/Components/Cards/AIOverviewCard';
import { COLOR_PRIMARY_2, COLOR_TEXT_2, COLOR_GREY_200 } from 'src/utils/colors';

const CHART_HEIGHT = 280;
const BAR_COLOR = '#0097B2';
const CURRENCY_SYMBOL = '$';

// Occupancy: 0-10%, 10-20%, ..., 90-100%
const OCCUPANCY_DISTRIBUTION = [
  { range: '0-10%', count: 42 },
  { range: '10-20%', count: 88 },
  { range: '20-30%', count: 156 },
  { range: '30-40%', count: 224 },
  { range: '40-50%', count: 312 },
  { range: '50-60%', count: 398 },
  { range: '60-70%', count: 365 },
  { range: '70-80%', count: 278 },
  { range: '80-90%', count: 192 },
  { range: '90-100%', count: 95 },
];

// ADR: $0-$50, $50-100, $100-200, $200-300, $300-400, $400-600, $600-1000, $1000+
const ADR_DISTRIBUTION = [
  { range: '$0-50', count: 65 },
  { range: '$50-100', count: 218 },
  { range: '$100-200', count: 445 },
  { range: '$200-300', count: 382 },
  { range: '$300-400', count: 248 },
  { range: '$400-600', count: 156 },
  { range: '$600-1k', count: 72 },
  { range: '$1k+', count: 24 },
];

// Revenue: <$5k, $5k-10k, $10-20k, $20-40k, $40-60k, $60-80k, $80-100k, $100-150k, $150-200k, $200-300k, $300k+
const REVENUE_DISTRIBUTION = [
  { range: '<$5k', count: 88 },
  { range: '$5k-10k', count: 142 },
  { range: '$10-20k', count: 268 },
  { range: '$20-40k', count: 325 },
  { range: '$40-60k', count: 278 },
  { range: '$60-80k', count: 198 },
  { range: '$80-100k', count: 124 },
  { range: '$100-150k', count: 95 },
  { range: '$150-200k', count: 52 },
  { range: '$200-300k', count: 28 },
  { range: '$300k+', count: 12 },
];

// Active days: 1-60, 60-120, 120-180, 180-240, 240-300, 300-365
const ACTIVE_DAYS_DISTRIBUTION = [
  { range: '1-60', count: 125 },
  { range: '60-120', count: 198 },
  { range: '120-180', count: 312 },
  { range: '180-240', count: 385 },
  { range: '240-300', count: 278 },
  { range: '300-365', count: 402 },
];

const axisTickStyle = {
  fontSize: 12,
  fontFamily: 'Roboto',
  fill: COLOR_TEXT_2,
  fontWeight: 500,
};

const CountTooltip = ({ active, payload, labelKey = 'range', valueKey = 'count', suffix = ' listings' }: any) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <Box sx={{ background: '#fff', p: 1.5, borderRadius: 2, boxShadow: 2 }}>
      <Typography variant="body2" fontWeight={600} color={COLOR_TEXT_2}>
        {p[labelKey]}: {p[valueKey]}{suffix}
      </Typography>
    </Box>
  );
};

export interface DistributionChartsProps {
  isMobileView?: boolean;
  cachedMarketData?: any;
}

const DistributionCharts: React.FC<DistributionChartsProps> = ({ isMobileView = false, cachedMarketData }) => {
  const activeDaysData = React.useMemo(() => {
    const activeDays = cachedMarketData?.data?.supply_breakdown?.active_days_count;
    if (activeDays && typeof activeDays === 'object') {
      const rangeOrder = ['1-60', '60-120', '120-180', '180-240', '240-300', '300-365'];
      const mapped: Array<{ range: string; count: number }> = [];
      rangeOrder.forEach((range) => {
        const val = (activeDays as Record<string, number>)[range];
        if (val != null) mapped.push({ range, count: Number(val) });
      });
      if (mapped.length) return mapped;
    }
    return ACTIVE_DAYS_DISTRIBUTION;
  }, [cachedMarketData]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
          Occupancy rate distribution
        </Typography>
        <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
          <Box sx={{ width: '100%', height: CHART_HEIGHT, p: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={OCCUPANCY_DISTRIBUTION} margin={{ top: 24, right: 24, left: 0, bottom: 20 }} barCategoryGap="10%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
                <XAxis dataKey="range" axisLine={false} tickLine={false} tick={axisTickStyle} angle={-35} textAnchor="end" height={60} />
                <YAxis tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} label={{ value: 'Listings', angle: -90, position: 'insideLeft', style: { fontSize: 12, fontWeight: 600, fill: COLOR_TEXT_2 } }} />
                <Tooltip content={<CountTooltip />} cursor={false} />
                <Bar dataKey="count" fill={BAR_COLOR} radius={[4, 4, 0, 0]} barSize={24} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </AIOverviewCard>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
          ADR distribution
        </Typography>
        <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
          <Box sx={{ width: '100%', height: CHART_HEIGHT, p: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ADR_DISTRIBUTION} margin={{ top: 24, right: 24, left: 0, bottom: 20 }} barCategoryGap="15%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
                <XAxis dataKey="range" axisLine={false} tickLine={false} tick={axisTickStyle} />
                <YAxis tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} label={{ value: 'Listings', angle: -90, position: 'insideLeft', style: { fontSize: 12, fontWeight: 600, fill: COLOR_TEXT_2 } }} />
                <Tooltip content={<CountTooltip />} cursor={false} />
                <Bar dataKey="count" fill={BAR_COLOR} radius={[6, 6, 0, 0]} barSize={32} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </AIOverviewCard>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
          Revenue distribution
        </Typography>
        <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
          <Box sx={{ width: '100%', height: CHART_HEIGHT, p: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={REVENUE_DISTRIBUTION} margin={{ top: 24, right: 24, left: 0, bottom: 20 }} barCategoryGap="8%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
                <XAxis dataKey="range" axisLine={false} tickLine={false} tick={axisTickStyle} angle={-35} textAnchor="end" height={60} />
                <YAxis tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} label={{ value: 'Listings', angle: -90, position: 'insideLeft', style: { fontSize: 12, fontWeight: 600, fill: COLOR_TEXT_2 } }} />
                <Tooltip content={<CountTooltip />} cursor={false} />
                <Bar dataKey="count" fill={BAR_COLOR} radius={[4, 4, 0, 0]} barSize={20} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </AIOverviewCard>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: COLOR_PRIMARY_2, fontSize: '1rem', mb: 1.5 }}>
          Active days distribution
        </Typography>
        <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
          <Box sx={{ width: '100%', height: CHART_HEIGHT, p: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={activeDaysData} margin={{ top: 24, right: 24, left: 0, bottom: 20 }} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GREY_200} />
                <XAxis dataKey="range" axisLine={false} tickLine={false} tick={axisTickStyle} />
                <YAxis tickLine={false} axisLine={{ stroke: COLOR_TEXT_2, strokeDasharray: '4 4' }} tick={axisTickStyle} label={{ value: 'Listings', angle: -90, position: 'insideLeft', style: { fontSize: 12, fontWeight: 600, fill: COLOR_TEXT_2 } }} />
                <Tooltip content={<CountTooltip />} cursor={false} />
                <Bar dataKey="count" fill={BAR_COLOR} radius={[6, 6, 0, 0]} barSize={40} maxBarSize={56} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </AIOverviewCard>
      </Box>
    </Box>
  );
};

export default DistributionCharts;
